import { safeSupabaseCall, supabase } from "../supabaseClient";
import logger from "../utils/logger";

export const SUPPORT_CHAT_PRESENCE_WINDOW_MS = 20_000;
const SUPPORT_SCHEMA_ERROR_WINDOW_MS = 5 * 60 * 1000;
const supportSchemaErrorLogCache = new Map();

const getPresenceField = (readerRole = "user") =>
  readerRole === "admin" ? "admin_last_viewed_at" : "user_last_viewed_at";

const parseTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getSchemaErrorCacheKey = (chatId, readerRole, error) =>
  [chatId || "unknown", readerRole || "unknown", error?.code || "no-code"]
    .join(":");

export const isSupportSchemaMismatchError = (error) =>
  error?.code === "42703" ||
  error?.code === "42P01" ||
  error?.code === "PGRST202" ||
  /does not exist|could not find the function|schema cache/i.test(
    error?.message || "",
  );

const logSupportSchemaMismatchOnce = (chatId, readerRole, error) => {
  const cacheKey = getSchemaErrorCacheKey(chatId, readerRole, error);
  const now = Date.now();
  const lastLoggedAt = supportSchemaErrorLogCache.get(cacheKey) || 0;

  if (now - lastLoggedAt < SUPPORT_SCHEMA_ERROR_WINDOW_MS) {
    return;
  }

  supportSchemaErrorLogCache.set(cacheKey, now);
  logger.warn("Support chat schema mismatch; presence update skipped", {
    chatId,
    readerRole,
    error_code: error?.code || null,
    error_message: error?.message || null,
  });
};

export const shouldSuppressSupportPush = (
  chat,
  recipientRole = "user",
  now = Date.now(),
) => {
  const field = getPresenceField(recipientRole);
  const viewedAt = parseTimestamp(chat?.[field]);
  if (!viewedAt) return false;

  return now - viewedAt.getTime() <= SUPPORT_CHAT_PRESENCE_WINDOW_MS;
};

export const markSupportChatViewed = async (
  chatId,
  readerRole = "user",
) => {
  try {
    if (!chatId) {
      return null;
    }

    const field = getPresenceField(readerRole);
    return await safeSupabaseCall(() =>
      supabase
        .from("support_chats")
        .update({ [field]: new Date().toISOString() })
        .eq("id", chatId),
    );
  } catch (error) {
    if (isSupportSchemaMismatchError(error)) {
      logSupportSchemaMismatchOnce(chatId, readerRole, error);
      return null;
    }

    logger.error("Failed to mark support chat as viewed", {
      chatId,
      readerRole,
      error: error?.message,
      error_code: error?.code || null,
    });
    return null;
  }
};
